import { z } from 'zod'
import { Timestamp } from './common.js'
import { DisplayName } from './user.js'
import { Seasonality } from './seasonality.js'
import { Region, NaturalFeatures } from './spatial.js'
import { GeometryResponse, RouteLinePoint, RoutePointInput } from './waypoint.js'

// Re-export spatial types so existing `import { Region } from './route.js'`
// callers keep working during migration.
export { Region, NaturalFeatureKind, NaturalFeature, NaturalFeatures } from './spatial.js'

// Route + MediaAsset entities.
//
// Route shape is intentionally smaller than the legacy entity:
// large freeform description and the GPX geometry live as blob references
// (description_url, geometry_url) so the metadata.json stays cheap to read
// + hash for the canonical ETag. MediaAsset is a sibling document referenced
// by route_id.

export const Activity = z.enum(['hiking', 'running', 'cycling'])
export type Activity = z.infer<typeof Activity>

export const RouteStatus = z.enum(['draft', 'pending_review', 'published', 'archived'])
export type RouteStatus = z.infer<typeof RouteStatus>

export const PUBLICATION_LICENSE_VERSION = '2026-08-24'

export const RoutePublishConsentRequest = z
  .object({
    accepted: z.literal(true),
    license_version: z.literal(PUBLICATION_LICENSE_VERSION),
    accepted_etag: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .strict()
export type RoutePublishConsentRequest = z.infer<typeof RoutePublishConsentRequest>

export const Difficulty = z.enum(['easy', 'moderate', 'hard', 'expert'])
export type Difficulty = z.infer<typeof Difficulty>

export const RouteType = z.enum(['loop', 'point_to_point', 'out_and_back'])
export type RouteType = z.infer<typeof RouteType>

export const GeometryType = z.enum(['line'])
export type GeometryType = z.infer<typeof GeometryType>

export const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/

export const GpxStats = z.object({
  pointCount: z.number().int().nonnegative(),
  distanceKm: z.number().nonnegative().optional(),
  ascentM: z.number().nonnegative().optional(),
  descentM: z.number().nonnegative().optional(),
  durationSeconds: z
    .number()
    .nonnegative()
    .optional()
    .describe(
      'Sum of within-segment GPX elapsed seconds with complete, nondecreasing timestamps; not moving time or a route duration estimate.',
    ),
  recordedAt: Timestamp.optional(),
})
export type GpxStats = z.infer<typeof GpxStats>

export const TitleCandidate = z.object({
  title: z.string().min(1).max(200),
  source: z.enum(['gpx', 'peak+activity', 'city+activity', 'llm', 'fallback']),
  score: z.number().optional(),
})
export type TitleCandidate = z.infer<typeof TitleCandidate>

export const RouteEnrichmentResult = z.object({
  stats: GpxStats,
  region: Region.optional(),
  natural_features: NaturalFeatures.optional(),
  classified_activities: z.array(Activity).min(1),
  title_candidates: z.array(TitleCandidate).min(1),
  description_draft_md: z.string().optional(),
  enrich_version: z.literal('2026-06-06'),
})
export type RouteEnrichmentResult = z.infer<typeof RouteEnrichmentResult>

// Standalone AI peak sketch — free-text description in, a generated pen-and-ink
// line-art image out. Unlike cover-image this is not tied to a route: the image
// is persisted to the PRIVATE bucket and served back through a same-origin API
// proxy (GET /api/ai/peak-sketch/:id), so `image_url` is a relative path, not a
// public bucket URL (no dependency on the canonical public bucket or its CNAME).
export const PeakSketchRequest = z.object({
  description: z.string().min(1).max(2000),
  // Optional override of the drawing medium/palette/paper (e.g. "white paper,
  // fern-green colored pencil, minimal linework"). When absent the server uses
  // its default style. Lets the look be iterated per-request without a redeploy.
  style: z.string().max(600).optional(),
  // When true, the near-white paper is knocked out to alpha and the strokes are
  // recolored to the app's fern green, yielding a transparent, tintable line-art
  // PNG that reads on both light and dark surfaces, independent of model alpha support.
  transparent: z.boolean().optional(),
})
export type PeakSketchRequest = z.infer<typeof PeakSketchRequest>

export const GeneratedPeakSketch = z.object({
  id: z.string().uuid(),
  image_url: z.string(), // same-origin API path, e.g. /api/ai/peak-sketch/<id>
  content_type: z.string().min(1).max(128),
  size_bytes: z.number().int().nonnegative(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  created_at: Timestamp,
})
export type GeneratedPeakSketch = z.infer<typeof GeneratedPeakSketch>

// Share-card mountain backgrounds — a set of pre-baked black-ink-on-cream
// posters (derived from the peak sketches) stored in the PRIVATE bucket under
// `share-card-bg/<uuid>.jpg` (+ `<uuid>_thumb.jpg`), served back through
// same-origin PUBLIC proxies. The app fetches a random handful for the user to
// pick a share-card background from. URLs are same-origin API paths (like
// GeneratedPeakSketch.image_url), not public bucket URLs.
export const ShareBackground = z.object({
  id: z.string().uuid(),
  thumb_url: z.string(), // /api/ai/share-backgrounds/<id>/thumb
  full_url: z.string(), // /api/ai/share-backgrounds/<id>
})
export type ShareBackground = z.infer<typeof ShareBackground>

export const ShareBackgroundList = z.object({
  items: z.array(ShareBackground),
})
export type ShareBackgroundList = z.infer<typeof ShareBackgroundList>

// Geometry-derived location, persisted on the route doc so discover/list reads
// return route coordinates without fetching every geometry blob. `bbox` is
// [minLon, minLat, maxLon, maxLat]; `map_center` is its midpoint (the pin anchor).
export const DiscoverBbox = z.tuple([z.number(), z.number(), z.number(), z.number()])
export type DiscoverBbox = z.infer<typeof DiscoverBbox>
export const MapCenter = z.object({ lon: z.number(), lat: z.number() })
export type MapCenter = z.infer<typeof MapCenter>

// Canonical metadata.json contents — what gets hashed for the business ETag.
// Media is stored in a sibling Mongo collection keyed by route_id; clients
// fetch /api/media?route_id=<id> instead of denormalising into the route
// document, so adding/removing media never invalidates the route ETag.
const MediaReadUrl = z.union([
  z.string().url(),
  z
    .string()
    .regex(
      /^\/api\/media\/[a-f0-9-]{36}\/[a-f0-9-]{36}(?:\.(?:thumb|medium)-webp-v1-[a-f0-9]{64})?\/blob(?:\/(?:thumb|medium)\/[a-f0-9]{64})?(?:\?[^#\s]*)?$/i,
    ),
])

export const RouteMetadata = z.object({
  id: z.string().uuid(),
  schema_version: z.string(),
  title: z.string().min(1).max(200),
  slug: z.string().max(64).regex(SLUG_PATTERN),
  summary: z.string().max(2000),
  activities: z.array(Activity).min(1),
  status: RouteStatus,
  region: Region.optional(),
  natural_features: NaturalFeatures.optional(),
  difficulty: Difficulty.optional(),
  route_type: RouteType.optional(),
  distance_km: z.number().nonnegative().optional(),
  ascent_m: z.number().nonnegative().optional(),
  descent_m: z.number().nonnegative().optional(),
  duration_seconds: z.number().nonnegative().optional(),
  point_count: z.number().int().nonnegative().optional(),
  route_point_count: z.number().int().nonnegative().optional(),
  geometry_type: GeometryType.optional(),
  cover_image_url: MediaReadUrl.optional(),
  description_url: z.string().url().optional(),
  geometry_url: z.string().url().optional(),
  seasonality: Seasonality.optional(),
  map_center: MapCenter.optional(),
  bbox: DiscoverBbox.optional(),
  created_by: z.string().uuid(),
  creator_username: z.string().nullable().optional(),
  created_at: Timestamp,
  updated_at: Timestamp,
})
export type RouteMetadata = z.infer<typeof RouteMetadata>

// Mongo route document: metadata + legacy `pk` lookup key + canonical sha256
// ETag so list / point reads return the version clients can use for If-Match.
export const RouteDoc = RouteMetadata.extend({
  pk: z.string(),
  etag: z.string(),
})
export type RouteDoc = z.infer<typeof RouteDoc>

// Public projection — drops pk; keeps etag for client-side optimistic ops.
// Creator identity fields are server-resolved conveniences so clients can
// render public names without an extra /api/users/:id round-trip. Keep
// `creator_username` as the internal handle and `creator_name` as the
// user-visible display name.
export const RoutePublic = RouteMetadata.extend({
  etag: z.string(),
  creator_username: z.string().nullable().optional(),
  creator_name: DisplayName.nullable().optional(),
  description_attribution: z.string().optional(),
})
export type RoutePublic = z.infer<typeof RoutePublic>

// Trimmed list shape for discover/list responses.
export const RouteListItem = z.object({
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  summary: z.string(),
  activities: z.array(Activity),
  status: RouteStatus,
  difficulty: Difficulty.optional(),
  route_type: RouteType.optional(),
  distance_km: z.number().optional(),
  ascent_m: z.number().optional(),
  descent_m: z.number().optional(),
  route_point_count: z.number().int().nonnegative().optional(),
  geometry_type: GeometryType.optional(),
  cover_image_url: MediaReadUrl.optional(),
  geometry_url: z.string().url().optional(),
  created_by: z.string().uuid(),
  creator_username: z.string().nullable().optional(),
  creator_name: DisplayName.nullable().optional(),
  created_at: Timestamp,
  updated_at: Timestamp,
  favorite_count: z.number().int().nonnegative().default(0),
  etag: z.string(),
})
export type RouteListItem = z.infer<typeof RouteListItem>

export const DiscoverRouteListItem = RouteListItem.extend({
  region: Region.optional(),
  seasonality: Seasonality.optional(),
  map_center: MapCenter.optional(),
  bbox: DiscoverBbox.optional(),
  thumbnail_points: z.array(RouteLinePoint).max(100).optional(),
  thumbnail_draw_line: z.boolean().optional(),
})
export type DiscoverRouteListItem = z.infer<typeof DiscoverRouteListItem>

export const DiscoverRouteListResponse = z.object({
  items: z.array(DiscoverRouteListItem),
  /** Keyset pagination cursor; present when more pages remain (non-bbox list). */
  next_cursor: z.string().nullish(),
})
export type DiscoverRouteListResponse = z.infer<typeof DiscoverRouteListResponse>

// Wire payloads. Create accepts everything except server-assigned identity
// fields (id, created_by from auth, created_at/updated_at from clock, etag
// from canonical hash). Patch is a partial allowlist; status changes go
// through the same endpoint for now.

export const RouteCreateRequest = z.object({
  title: z.string().min(1).max(200),
  slug: z.string().max(64).regex(SLUG_PATTERN).optional(),
  summary: z.string().max(2000).default(''),
  activities: z.array(Activity).min(1),
  status: RouteStatus.default('draft'),
  region: Region.optional(),
  natural_features: NaturalFeatures.optional(),
  difficulty: Difficulty.optional(),
  route_type: RouteType.optional(),
  distance_km: z.number().nonnegative().optional(),
  ascent_m: z.number().nonnegative().optional(),
  descent_m: z.number().nonnegative().optional(),
  duration_seconds: z.number().nonnegative().optional(),
  point_count: z.number().int().nonnegative().optional(),
  route_point_count: z.number().int().nonnegative().optional(),
  geometry_type: GeometryType.optional(),
  cover_image_url: MediaReadUrl.optional(),
  description_url: z.string().url().optional(),
  geometry_url: z.string().url().optional(),
  seasonality: Seasonality.optional(),
})
export type RouteCreateRequest = z.infer<typeof RouteCreateRequest>

export const RouteFromGeometryRequest = z
  .object({
    activities: z.array(Activity).min(1),
    title: z.string().min(1).max(200).optional(),
    geometry_type: GeometryType,
    line: z.array(RouteLinePoint).min(2).max(10000),
    points: z.array(RoutePointInput).max(100).default([]),
    source: z
      .object({
        kind: z.literal('drawn'),
        routing_mode: z.enum(['manual', 'routing']).optional(),
        routing_engine: z.enum(['graphhopper', 'brouter']).optional(),
      })
      .optional(),
  })
  .strict()
export type RouteFromGeometryRequest = z.input<typeof RouteFromGeometryRequest>

export const RouteFromGeometryResponse = z.object({
  route: RoutePublic,
  etag: z.string(),
  geometry: GeometryResponse,
  enrich: RouteEnrichmentResult.nullable().optional(),
})
export type RouteFromGeometryResponse = z.input<typeof RouteFromGeometryResponse>

export const RoutingPreviewRequest = z
  .object({
    engine: z.enum(['graphhopper', 'brouter']).default('graphhopper'),
    profile: z.enum(['foot', 'bike']).default('foot'),
    points: z.array(RouteLinePoint).min(2).max(500),
  })
  .strict()
export type RoutingPreviewRequest = z.input<typeof RoutingPreviewRequest>

export const RoutingPreviewResponse = z.object({
  line: z.array(RouteLinePoint).min(2).max(10000),
  source: z.object({
    engine: z.enum(['graphhopper', 'brouter']),
    profile: z.enum(['foot', 'bike']),
  }),
})
export type RoutingPreviewResponse = z.input<typeof RoutingPreviewResponse>

export const RoutingErrorCode = z.enum([
  'routing_point_unmatched',
  'routing_connection_not_found',
  'routing_distance_exceeded',
  'routing_timeout',
  'routing_upstream_error',
  'routing_too_dense',
])
export type RoutingErrorCode = z.infer<typeof RoutingErrorCode>

export const RoutingErrorResponse = z.object({
  error: RoutingErrorCode,
  message: z.string(),
})
export type RoutingErrorResponse = z.infer<typeof RoutingErrorResponse>

export const RoutePatchRequest = z
  .object({
    title: z.string().min(1).max(200),
    slug: z.string().max(64).regex(SLUG_PATTERN),
    summary: z.string().max(2000),
    activities: z.array(Activity).min(1),
    status: RouteStatus,
    region: Region,
    natural_features: NaturalFeatures,
    difficulty: Difficulty,
    route_type: RouteType,
    distance_km: z.number().nonnegative(),
    ascent_m: z.number().nonnegative(),
    descent_m: z.number().nonnegative(),
    duration_seconds: z.number().nonnegative(),
    point_count: z.number().int().nonnegative(),
    route_point_count: z.number().int().nonnegative(),
    geometry_type: GeometryType,
    cover_image_url: MediaReadUrl,
    description_url: z.string().url(),
    geometry_url: z.string().url(),
    seasonality: Seasonality,
  })
  .partial()
export type RoutePatchRequest = z.infer<typeof RoutePatchRequest>

// MediaAsset — sibling document referenced by route_id. /api/routes responses
// do not embed media; clients fetch /api/media?route_id=<id> so route metadata
// stays cheap to hash + ETag.

export const MediaKind = z.enum(['image', 'video', 'audio'])
export type MediaKind = z.infer<typeof MediaKind>

export const ImageVariant = z.object({
  blob_path: z.string().min(1),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  size_bytes: z.number().int().positive(),
  etag: z.string().regex(/^[a-f0-9]{64}$/),
})
export const ImageVariants = z.object({
  version: z.literal('webp-v1'),
  thumb: ImageVariant,
  medium: ImageVariant,
})
export type ImageVariants = z.infer<typeof ImageVariants>

const plainMediaId = /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i
const variantMediaId =
  /^([a-fA-F0-9]{8}(?:-[a-fA-F0-9]{4}){3}-[a-fA-F0-9]{12})\.(thumb|medium)-webp-v1-([a-f0-9]{64})$/
export const MediaBlobId = z.union([
  z.string().regex(plainMediaId),
  z.string().regex(variantMediaId),
])

export function parseMediaBlobId(value: string): {
  id: string
  variant?: 'thumb' | 'medium'
  version?: string
} | null {
  if (plainMediaId.test(value)) return { id: value }
  const matched = variantMediaId.exec(value)
  if (!matched) return null
  return { id: matched[1]!, variant: matched[2] as 'thumb' | 'medium', version: matched[3]! }
}

export function mediaVariantUrl(
  routeId: string,
  id: string,
  variant: 'thumb' | 'medium',
  version: string,
) {
  return `/api/media/${routeId}/${id}.${variant}-webp-v1-${version}/blob`
}

export function parseMediaProxyPath(path: string) {
  const parts = path.split('/')
  if (
    parts.length !== 6 ||
    parts[0] !== '' ||
    parts[1] !== 'api' ||
    parts[2] !== 'media' ||
    parts[5] !== 'blob' ||
    !plainMediaId.test(parts[3]!)
  )
    return null
  const media = parseMediaBlobId(parts[4]!)
  return media ? { routeId: parts[3]!, ...media } : null
}

export const MediaAssetDoc = z.object({
  id: z.string().uuid(),
  pk: z.string(),
  route_id: z.string().uuid(),
  waypoint_id: z.string().uuid().optional(),
  kind: MediaKind,
  blob_url: z.string().url(),
  blob_path: z.string(),
  image_variants: ImageVariants.optional(),
  image_cleanup_paths: z.array(z.string()).optional(),
  content_type: z.string().min(1).max(128),
  size_bytes: z.number().int().nonnegative(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  duration_seconds: z.number().nonnegative().optional(),
  captured_at: Timestamp.optional(),
  alt_text: z.string().max(500).optional(),
  uploaded_by: z.string().uuid(),
  created_at: Timestamp,
})
export type MediaAssetDoc = z.infer<typeof MediaAssetDoc>

export const MediaAssetPublic = MediaAssetDoc.omit({
  pk: true,
  blob_path: true,
  image_variants: true,
  image_cleanup_paths: true,
}).extend({
  blob_url: MediaReadUrl,
  thumb_url: MediaReadUrl.optional(),
  medium_url: MediaReadUrl.optional(),
})
export type MediaAssetPublic = z.infer<typeof MediaAssetPublic>

export function mediaToPublic(doc: MediaAssetDoc): MediaAssetPublic {
  const {
    pk: _pk,
    blob_path: _bp,
    image_variants: variants,
    image_cleanup_paths: _cleanup,
    account_deletion_id: _deletion,
    ...rest
  } = doc as MediaAssetDoc & { account_deletion_id?: string }
  const base = `/api/media/${doc.route_id}/${doc.id}/blob`
  if (doc.kind === 'image' && variants) {
    const thumb = mediaVariantUrl(doc.route_id, doc.id, 'thumb', variants.thumb.etag)
    const medium = mediaVariantUrl(doc.route_id, doc.id, 'medium', variants.medium.etag)
    return {
      ...rest,
      blob_url: medium,
      thumb_url: thumb,
      medium_url: medium,
      size_bytes: variants.medium.size_bytes,
      width: variants.medium.width,
      height: variants.medium.height,
    }
  }
  return { ...rest, blob_url: base }
}

export function routeToPublic(
  doc: RouteDoc,
  opts: { creator_username?: string | null; creator_name?: string | null } = {},
): RoutePublic {
  const {
    pk: _pk,
    edit_contributors: _contributors,
    publication_receipt: _receipt,
    account_deletion_owner: _deletion,
    account_deletion_retain: _retain,
    ...rest
  } = doc as RouteDoc & {
    edit_contributors?: Record<string, string>
    publication_receipt?: unknown
    account_deletion_owner?: string
    account_deletion_retain?: boolean
  }
  return {
    ...withGeometryProjection(rest),
    creator_username: opts.creator_username ?? null,
    creator_name: opts.creator_name ?? null,
  }
}

export function routeToListItem(
  doc: RouteDoc,
  favoriteCount = 0,
  opts: { creator_username?: string | null; creator_name?: string | null } = {},
): RouteListItem {
  const projected = withGeometryProjection(doc)
  return {
    id: projected.id,
    title: projected.title,
    slug: projected.slug,
    summary: projected.summary,
    activities: projected.activities,
    status: projected.status,
    difficulty: projected.difficulty,
    route_type: projected.route_type,
    distance_km: projected.distance_km,
    ascent_m: projected.ascent_m,
    descent_m: projected.descent_m,
    route_point_count: projected.route_point_count,
    geometry_type: projected.geometry_type,
    cover_image_url: projected.cover_image_url,
    geometry_url: projected.geometry_url,
    created_by: projected.created_by,
    creator_username: opts.creator_username ?? null,
    creator_name: opts.creator_name ?? null,
    created_at: projected.created_at,
    updated_at: projected.updated_at,
    favorite_count: favoriteCount,
    etag: projected.etag,
  }
}

function withGeometryProjection<
  T extends { geometry_url?: string; geometry_type?: GeometryType; route_point_count?: number },
>(route: T): T {
  if (!route.geometry_url) return route
  return {
    ...route,
    geometry_type: route.geometry_type ?? 'line',
    route_point_count: route.route_point_count ?? 0,
  } as T
}
